<template>
  <li :class="classes">
    <div class="former-section-item-content">
      <label
        class="former-section-item-label"
        :for="id"
      >
        {{ item.label }}<span
          v-if="required && editable"
          class="former-section-item-required"
        >*</span>

        <spinner-animated
          v-if="item.processing"
          class="former-section-item-spinner"
          color="gray"
          size="small"
        />
      </label>

      <span
        :is="type"
        v-if="editable && desktop"
        :id="id"
        ref="input"
        :desktop="desktop"
        :disabled="item.processing || item.disabled"
        :field="item"
        :value="item.value"
        @change="change"
      />

      <span
        v-else
        :class="valueClasses"
      >
        {{ text }}

        <font-awesome-icon
          v-if="editable && !item.disabled"
          class="former-section-item-icon"
          icon="pen"
        />
      </span>

      <button
        v-if="copyable && copyEnabled"
        key="copyable"
        :class="coypableClasses"
        @click="copy"
      >
        <font-awesome-icon :icon="copied ? 'check' : 'clone'" />
      </button>
    </div>

    <span
      v-if="item.errors.length"
      class="former-section-item-error"
    >
      <former-field-error
        :error="item.errors[0]"
      />
    </span>

    <span
      v-else-if="item.validated"
      class="former-section-item-validated"
    >
      <font-awesome-icon
        icon="check-circle"
      />

      {{ typeof item.validated === 'string' ? item.validated : 'Válido' }}
    </span>
  </li>
</template>

<script>
  import * as filters from '@/helpers/filters';

  import FormerInput from '@/components/Former/Field/Input.vue';
  import FormerBoolean from '@/components/Former/Field/Boolean.vue';
  import FormerSelect from '@/components/Former/Field/Select.vue';
  import FormerFieldError from '@/components/Former/Field/Error.vue';

  import SpinnerAnimated from '@/components/Spinner/Animated.vue';

  export default {
    name: 'FormerSectionItem',
    components: {
      FormerFieldError,
      FormerInput,
      FormerBoolean,
      FormerSelect,
      SpinnerAnimated,
    },
    props: {
      item: {
        type: Object,
        default() {
          return {};
        },
      },
      value: {
        type: String,
        default() {
          return '';
        },
      },
      desktop: {
        type: Boolean,
        default: false,
      },
      copyable: {
        type: Boolean,
        default: false,
      },
      editable: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        copied: false,
        copiedTimeout: null,
      };
    },
    computed: {
      id() {
        return this.item.id;
      },

      coypableClasses() {
        const classes = ['former-section-item-copy'];

        if (this.copied) {
          classes.push('former-section-item-copy-copied');
        }

        return classes;
      },

      classes() {
        const classes = ['former-section-item'];

        const {
          editable,
          item,
        } = this;

        const {
          errors,
        } = item;

        if (errors.length) {
          classes.push('former-section-item-red');
        } else if (item.validated) {
          classes.push('former-section-item-green');
        }

        classes.push(editable ? 'former-section-item-editable' : 'former-section-item-uneditable');

        return classes;
      },
      valueClasses() {
        const {
          customClasses = [],
        } = this.item;

        const classes = [
          ...customClasses,
          'former-section-item-value',
        ];


        if (this.empty) {
          if (this.editable) {
            classes.push('former-section-item-value-empty');
          } else {
            classes.push('former-section-item-value-readonly');
          }
        }

        return classes;
      },

      empty() {
        return ['', null, undefined].indexOf(this.item.value) > -1;
      },
      text() {
        const {
          options,
          mask,
          value,
        } = this.item;

        if (this.empty) {
          if (!this.editable) {
            return 'Em branco';
          }

          return undefined;
        }

        if (options) {
          let found;

          options.some((option) => {
            if (option.value === value) {
              found = option;
            }

            return !!found;
          });

          return found ? found.label : value;
        }

        if (mask && mask.type && filters[mask.type]) {
          return filters[mask.type](value, mask);
        }

        return value;
      },
      required() {
        return this.item.validations.some(validation => validation.type === 'blank');
      },

      type() {
        switch (this.item.type) {
          case 'boolean':
            return 'former-boolean';
          case 'select':
            return 'former-select';
          default:
            return 'former-input';
        }
      },

      copyEnabled() {
        return !!navigator.clipboard;
      },
    },
    beforeDestroy() {
      clearTimeout(this.copiedTimeout);
    },
    methods: {
      change(value) {
        this.$emit('change', this.item.id, value);
      },

      copy() {
        navigator.clipboard.writeText(this.text).then(this.copySuccess);
      },

      copySuccess() {
        this.copied = true;

        this.copiedTimeout = setTimeout(this.copyClear, 1000);
      },

      copyClear() {
        this.copied = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/_variables.scss';

  .former-section-item {
    margin-top: $gutter-small;
    position: relative;

    &:first-child {
      margin-top: 0;
    }
  }

  .former-section-item-copy {
    @include button-reset;

    align-items: center;
    background-color: $gray;
    bottom: 0;
    border-radius: 50%;
    color: $white;
    cursor: pointer;
    display: none;
    flex-direction: row;
    height: $button-smaller;
    justify-content: center;
    position: absolute;
    right: -$button-smaller + $gutter-smaller;
    text-align: center;
    top: 0;
    width: $button-smaller;

    .former-section-item:hover & {
      display: flex;
    }
  }

  .former-section-item-copy-copied {
    background-color: $green;
  }

  .former-section-item-content {
    .former-section-item-uneditable & {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: $gutter;
      justify-content: space-between;
    }
  }

  .former-section-item-label {
    display: block;
    margin-bottom: $gutter-small;
  }

  .former-section-item-red {
    color: $red;
    margin-bottom: $gutter;
    margin-top: $gutter;
  }

  .former-section-item-value {
    background-color: $gray-light;
    border-radius: $button-smaller * 0.5;
    margin-right: -$gutter-smaller;
    padding: $gutter-smaller $gutter;
    transition: background-color 0.1s, color 0.1s;
    word-break: break-word;

    .former-section-item-green & {
      color: $green;
    }

    .former-section-item-red & {
      background-color: $red;
      color: $white;
    }
  }

  .former-section-item-error {
    display: block;
    text-align: right;
    margin-top: $gutter-small;
  }

  .former-section-item-validated {
    color: $green;
    display: block;
    margin-top: $gutter-smallest;
    text-align: right;
  }

  .former-section-item-value-empty {
    background-color: $gray-light;
    color: $gray;
    height: $button-smaller;
    line-height: $button-smaller;
    text-align: center;
    width: $button-smaller;
    padding: 0;
  }

  .former-section-item-value-readonly {
    color: $gray;
  }

  .former-section-item-icon {
    font-size: $font-smallest;
    margin-left: $gutter-small;
    margin-right: -$gutter-smaller;
    vertical-align: 0.25em;

    .former-section-item-value-empty & {
      margin: 0;
    }
  }

  .former-section-item-required {
    color: $red;
  }

  .former-section-item-spinner {
    display: inline-block;
    margin-left: $gutter-smaller;
  }
</style>
